import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';

/**
 * Creates a debounced signal that follows the source signal but delays updates.
 * Useful for preventing excessive re-computations when a signal changes rapidly.
 *
 * @param source - The source signal to debounce
 * @param delay - Delay in milliseconds before updating the debounced signal
 * @returns A tuple of [debouncedSignal, dispose] where dispose cleans up resources
 */
export function debouncedSignal<T>(source: ReadonlySignal<T>, delay: number): [ReadonlySignal<T>, () => void] {
    const debounced = signal<T>(source.value);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Create an effect that watches the source signal
    const dispose = effect(() => {
        const value = source.value;

        // Clear any pending timeout
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }

        // Set up new timeout for debounced update
        timeoutId = setTimeout(() => {
            debounced.value = value;
            timeoutId = undefined;
        }, delay);
    });

    // Return cleanup function that disposes of effect and clears timeout
    const cleanup = () => {
        dispose();
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            // Ensure the last value is applied
            debounced.value = source.value;
        }
    };

    return [debounced, cleanup];
}
